import { defaultStringifySearch, useRouter, useSearch } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import type { ResendAllResult } from '@nest-recruit/shared';
import { Pager } from '../../../components/Pager';
import { SkeletonLines } from '../../../components/Skeleton';
import { EmptyState, FilteredEmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { EMPTY, formatNumber } from '../../../lib/format';
import { useMediaQuery } from '../../../lib/use-media-query';
import { useCanEdit, useCanView, useCurrentSession } from '../../../stores/session';
import { useFailureGroups, useMessageLog } from '../api/email-delivery-api';
import { EmailToolbar, TestSendButton } from '../components/email-delivery/EmailToolbar';
import { FailureCauses, ResendReport } from '../components/email-delivery/FailureCauses';
import { MessageList } from '../components/email-delivery/MessageList';
import { MessagePane } from '../components/email-delivery/MessagePane';
import {
  EMAIL_PAGE_SIZE,
  filterCountOf,
  logParamsOf,
  plural,
  readEmailDeliverySearch,
  type EmailDeliverySearch,
} from '../components/email-delivery/email-delivery-address';
import styles from './EmailDeliveryPage.module.css';

/**
 * ADMIN Email delivery (Admin redesign batch 2, Option A "Mail inbox with failures pinned on top"): every email the
 * system sends and whether it went. Failures stay on top until somebody sends them again, fixes the address or
 * dismisses them — "delivery failures logged and visible, never silent" (master plan S8).
 *
 * - Status pills with counts, then search, Sent to and When; all in the address (06-qol §3).
 * - "Why they failed", grouped by cause, one button each, and Send all again.
 * - The chosen email in a pane beside the list: the reason in plain words, Fix address, Send again, Dismiss.
 * - Delivered and Bounced are not shown: nothing sets them. There is no "SMTP settings": SMTP is set on the server.
 */

/** semantic.css's laptop width: below it the pane opens over the list. */
const OVERLAY_MEDIA = '(max-width: 1180px)';
const PATH = '/admin/email';

export function EmailDeliveryPage(): ReactNode {
  const canView = useCanView('admin_config');
  const canEdit = useCanEdit('admin_config');
  const session = useCurrentSession();
  const router = useRouter();
  const raw = useSearch({ strict: false }) as Readonly<Record<string, unknown>>;
  const search = readEmailDeliverySearch(raw);
  const isOverlay = useMediaQuery(OVERLAY_MEDIA);
  const [report, setReport] = useState<ResendAllResult | null>(null);
  // Clearing the filters starts the toolbar afresh, so the search box empties too.
  const [clears, setClears] = useState(0);

  // Nothing is asked of the server for someone who may not open the page.
  const log = useMessageLog(logParamsOf(search), canView);
  const causes = useFailureGroups(canView);

  const go = (next: EmailDeliverySearch): void => {
    router.history.replace(`${PATH}${defaultStringifySearch({ ...next })}`);
  };
  /** A filter change goes back to the first page. */
  const filterBy = (next: Partial<EmailDeliverySearch>): void => go({ ...search, ...next, page: undefined });
  const open = (id: string): void => go({ ...search, open: id });
  const close = (): void => go({ ...search, open: undefined });

  if (!canView) return <ForbiddenState resource="the email delivery log" contact="Your admin" />;

  const rows = log.data?.rows ?? [];
  const total = log.data?.total ?? 0;
  const page = search.page ?? 1;
  const pageCount = Math.max(1, Math.ceil(total / EMAIL_PAGE_SIZE));
  const filters = filterCountOf(search) + (search.state ? 1 : 0);
  const needsAttention = log.data?.needsAttention;
  const failedToday = log.data?.failedToday ?? 0;

  const pane =
    search.open !== undefined ? (
      <MessagePane key={search.open} id={search.open} row={rows.find((row) => row.id === search.open)} mode={isOverlay ? 'overlay' : 'docked'} canEdit={canEdit} onOpen={open} onClose={close} />
    ) : null;

  const list =
    log.isError && !log.data ? (
      <ServerErrorState requestId={log.error.requestId} onRetry={() => void log.refetch()} />
    ) : log.isPending ? (
      <div className={styles.loading}>
        <SkeletonLines count={8} />
      </div>
    ) : rows.length === 0 ? (
      filters > 0 ? (
        <FilteredEmptyState
          filterCount={filters}
          onClearFilters={() => {
            setClears(clears + 1);
            go({});
          }}
          isInset
        />
      ) : (
        <EmptyState
          title="No email has been sent yet"
          body={canEdit ? 'Every email the system sends appears here with whether it went. Send yourself a test to check the mail settings work.' : 'Every email the system sends appears here with whether it went.'}
          action={canEdit ? <TestSendButton email={session?.email ?? null} /> : null}
          isInset
        />
      )
    ) : (
      <>
        <MessageList rows={rows} isPinned={search.state === undefined} openId={search.open} onOpen={open} />
        {pageCount > 1 && <Pager page={page} pageCount={pageCount} pageSize={EMAIL_PAGE_SIZE} rowCount={rows.length} total={total} onPage={(next) => go({ ...search, page: next > 1 ? next : undefined })} />}
      </>
    );

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <span className={styles.crumb}>Admin · Messages</span>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Email delivery</h1>
          <span className={styles.total}>{log.data ? formatNumber(log.data.counts.all) : EMPTY}</span>
          <span className={styles.grow} />
          {/* Review W19: the test is a change (the server asks for Admin settings at create), so a look-only copy has none. */}
          {canEdit && <TestSendButton email={session?.email ?? null} />}
        </div>
        <p className={styles.lead}>
          Every email the system sends, and whether it went. Failures stay on top until somebody sends them again or dismisses them.
          {needsAttention !== undefined && needsAttention > 0 && (
            <>
              {' '}
              <b className={styles.alarm}>
                {plural(needsAttention, 'needs', 'need')} attention, {failedToday.toLocaleString()} from today.
              </b>
            </>
          )}
        </p>
      </header>

      <EmailToolbar key={clears} search={search} counts={log.data?.counts} onChange={filterBy} />

      <div className={styles.layout} data-pane={pane !== null && !isOverlay ? 'docked' : undefined}>
        <div className={styles.main}>
          {report && <ResendReport result={report} onOpen={open} onDismiss={() => setReport(null)} />}
          {causes.data && <FailureCauses groups={causes.data} canEdit={canEdit} onOpen={open} onResult={setReport} />}
          {list}
        </div>
        {pane}
      </div>

      {!canEdit && <p className={styles.footNote}>You can look at this log, but not send emails again or dismiss them.</p>}
    </div>
  );
}
